import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

export type ContextMenuItem =
  | { type: 'separator' }
  | {
      label: string
      action?: () => void
      shortcut?: string
      title?: string
      disabled?: boolean
      checked?: boolean
      submenu?: ContextMenuItem[]
    }

interface ContextMenuProps {
  x: number
  y: number
  items: ContextMenuItem[]
  onClose: () => void
  // Internal: submenus are rendered inside the parent and should not attach global click handlers.
  isSubmenu?: boolean
  // Internal: when this menu is a submenu, allow closing just the submenu.
  onCloseSubmenu?: () => void
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose, isSubmenu, onCloseSubmenu }) => {
  const ref = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [position, setPosition] = useState<{ x: number; y: number }>({ x, y })
  const [focusIdx, setFocusIdx] = useState<number>(() => {
    for (let i = 0; i < items.length; i++) {
      const it = items[i] as any
      if (it?.type === 'separator') continue
      if (it?.disabled) continue
      return i
    }
    return -1
  })
  const [submenu, setSubmenu] = useState<null | { x: number; y: number; items: ContextMenuItem[] }>(null)

  useEffect(() => {
    if (isSubmenu) return
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose, isSubmenu])

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      setPosition({ x, y })
      return
    }
    const node = ref.current
    if (!node) {
      setPosition({ x, y })
      return
    }
    const rect = node.getBoundingClientRect()
    const margin = 8
    let nextX = x
    let nextY = y
    const maxX = window.innerWidth - rect.width - margin
    const maxY = window.innerHeight - rect.height - margin
    if (Number.isFinite(maxX)) {
      nextX = Math.min(nextX, maxX)
    }
    if (Number.isFinite(maxY)) {
      nextY = Math.min(nextY, maxY)
    }
    nextX = Math.max(margin, nextX)
    nextY = Math.max(margin, nextY)
    setPosition({ x: nextX, y: nextY })
  }, [x, y, items.length])

  const focusableIndices = useMemo(() => {
    const idxs: number[] = []
    for (let i = 0; i < items.length; i++) {
      const it = items[i] as any
      if (it?.type === 'separator') continue
      if (it?.disabled) continue
      idxs.push(i)
    }
    return idxs
  }, [items])

  useEffect(() => {
    // Auto-focus first enabled item when menu mounts.
    const idx = focusIdx >= 0 ? focusIdx : (focusableIndices[0] ?? -1)
    if (idx < 0) return
    setFocusIdx(idx)
    const btn = itemRefs.current[idx]
    if (btn) btn.focus()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focusIdx < 0) return
    const btn = itemRefs.current[focusIdx]
    if (btn && document.activeElement !== btn) btn.focus()
  }, [focusIdx])

  const openSubmenuForIndex = (idx: number) => {
    const item = items[idx] as any
    const hasSubmenu = Array.isArray(item?.submenu) && item.submenu.length > 0
    if (!hasSubmenu) return false
    const el = itemRefs.current[idx]
    if (!el) return false
    const rect = el.getBoundingClientRect()
    setSubmenu({
      x: rect.right - 2,
      y: rect.top - 4,
      items: item.submenu,
    })
    return true
  }

  const closeThisMenu = () => {
    if (isSubmenu && onCloseSubmenu) onCloseSubmenu()
    else onClose()
  }

  const moveFocus = (delta: number) => {
    if (!focusableIndices.length) return
    const curPos = Math.max(0, focusableIndices.indexOf(focusIdx))
    const nextPos = (curPos + delta + focusableIndices.length) % focusableIndices.length
    setFocusIdx(focusableIndices[nextPos]!)
  }

  const menuStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'fixed',
      top: position.y,
      left: position.x,
      backgroundColor: 'white',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      border: '1px solid #ccc',
      borderRadius: 4,
      padding: '4px 0',
      zIndex: 1000,
      minWidth: 220,
      fontFamily: 'Arial, sans-serif',
    }),
    [position.x, position.y]
  )

  return (
    <div
      ref={ref}
      style={menuStyle}
      role="menu"
      tabIndex={-1}
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          closeThisMenu()
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          e.stopPropagation()
          moveFocus(1)
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          e.stopPropagation()
          moveFocus(-1)
          return
        }
        if (e.key === 'Home') {
          e.preventDefault()
          e.stopPropagation()
          const first = focusableIndices[0]
          if (typeof first === 'number') setFocusIdx(first)
          return
        }
        if (e.key === 'End') {
          e.preventDefault()
          e.stopPropagation()
          const last = focusableIndices[focusableIndices.length - 1]
          if (typeof last === 'number') setFocusIdx(last)
          return
        }
        if (e.key === 'ArrowLeft' && isSubmenu) {
          e.preventDefault()
          e.stopPropagation()
          closeThisMenu()
          return
        }
        if (e.key === 'ArrowRight') {
          if (focusIdx < 0) return
          const opened = openSubmenuForIndex(focusIdx)
          if (opened) {
            e.preventDefault()
            e.stopPropagation()
          }
          return
        }
        if (e.key === 'Enter' || e.key === ' ') {
          if (focusIdx < 0) return
          const item = items[focusIdx] as any
          if (!item || item.type === 'separator' || item.disabled) return
          const hasSubmenu = Array.isArray(item.submenu) && item.submenu.length > 0
          e.preventDefault()
          e.stopPropagation()
          if (hasSubmenu) {
            openSubmenuForIndex(focusIdx)
            return
          }
          item.action?.()
          onClose()
        }
      }}
    >
      {items.map((item, index) => {
        if ((item as any)?.type === 'separator') {
          return (
            <div
              key={`sep_${index}`}
              role="separator"
              style={{ margin: '4px 0', borderTop: '1px solid #e5e7eb' }}
            />
          )
        }

        const it = item as Exclude<ContextMenuItem, { type: 'separator' }>
        const hasSubmenu = Array.isArray(it.submenu) && it.submenu.length > 0
        const disabled = Boolean(it.disabled)
        const checked = Boolean(it.checked)
        const focused = focusIdx === index

        const rowStyle: React.CSSProperties = {
          padding: '7px 12px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 13,
          color: disabled ? '#9ca3af' : '#111827',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          gap: 12,
          background: focused ? '#f3f4f6' : 'transparent',
        }

        return (
          <button
            key={`item_${index}`}
            ref={(el) => { itemRefs.current[index] = el }}
            type="button"
            role="menuitem"
            tabIndex={focused ? 0 : -1}
            aria-disabled={disabled || undefined}
            aria-haspopup={hasSubmenu ? 'menu' : undefined}
            title={it.title ?? it.label}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (disabled) return
              if (hasSubmenu) return
              it.action?.()
              onClose()
            }}
            onMouseEnter={() => {
              setFocusIdx(index)
              if (hasSubmenu) {
                openSubmenuForIndex(index)
              } else {
                setSubmenu(null)
              }
            }}
            onFocus={() => setFocusIdx(index)}
            style={{
              ...rowStyle,
              width: '100%',
              border: 'none',
              backgroundColor: rowStyle.background as any,
              textAlign: 'left',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ width: 14, display: 'inline-block', textAlign: 'center' }}>
                {checked ? '✓' : ''}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {it.shortcut ? (
                <span style={{ color: '#6b7280', fontSize: 11 }}>{it.shortcut}</span>
              ) : null}
              {hasSubmenu ? <span style={{ color: '#6b7280' }}>{'›'}</span> : null}
            </div>
          </button>
        )
      })}

      {submenu ? (
        <ContextMenu
          x={submenu.x}
          y={submenu.y}
          items={submenu.items}
          onClose={onClose}
          isSubmenu={true}
          onCloseSubmenu={() => setSubmenu(null)}
        />
      ) : null}
    </div>
  )
}
